import { Text } from './Text';
import { cx } from '@/lib/cx';
import { colors } from '@/theme/tokens';
import styles from './Segment.module.css';

interface SegmentOption<T> {
  value: T;
  label: string;
  /** Small line under the label, for a consequence worth stating. */
  sub?: string;
}

interface SegmentProps<T extends string | number | boolean> {
  value: T;
  options: SegmentOption<T>[];
  label: string;
  onChange: (value: T) => void;
}

/**
 * A two-or-three-way choice with every option visible at once.
 *
 * Replaces a toggle button that rendered only its current state: the alternative
 * was invisible until you tapped, and two defaulted-on toggles side by side read
 * as two selected buttons rather than two independent questions. If someone has
 * to press a control to discover what it does, the control is wrong.
 *
 * Same shape as the currency segment on the cart, which is where the pattern
 * came from.
 */
export function Segment<T extends string | number | boolean>({
  value,
  options,
  label,
  onChange,
}: SegmentProps<T>) {
  return (
    <div className={styles.segment} role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cx(styles.item, selected && styles.itemOn)}
          >
            <Text
              weight={selected ? 800 : 500}
              size={12.5}
              color={selected ? colors.surfaceAlt : colors.textMuted}
            >
              {option.label}
            </Text>
            {option.sub ? (
              <Text size={9.5} color={selected ? colors.onDarkBright : colors.textFaint}>
                {option.sub}
              </Text>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
